import {
  CourseFrequencyPayload,
  CourseFrequencyType,
  DailyFrequencyPayload,
  MonthlyFrequencyPayload,
  WeeklyFrequencyPayload,
} from '@beable/roster-backend-client-v1';
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { isEmpty } from 'lodash';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { getStyles } from '../styles';

interface FrequencyPayloadProps {
  payload?: CourseFrequencyPayload;
  handlePayloadChange(payload: CourseFrequencyPayload): void;
  lessonCount: number;
}

function getDays() {
  return ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
}

function Daily({ payload, handlePayloadChange, lessonCount }: FrequencyPayloadProps) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const [interval, setInterval] = useState<number>();
  const [quantityPerDate, setQuantityPerDate] = useState<number>();

  useEffect(() => {
    const dailyPayload = payload as DailyFrequencyPayload;
    if (dailyPayload && dailyPayload.interval) {
      setInterval(dailyPayload.interval)
    }
    if(dailyPayload && dailyPayload.quantityPerDate) {
      setQuantityPerDate(setQuantity(lessonCount, dailyPayload.quantityPerDate));
    }
  }, [payload, lessonCount])

  useEffect(() => {
    if (interval && interval > 0 && quantityPerDate && quantityPerDate > 0) {
      handlePayloadChange({ interval, quantityPerDate } as DailyFrequencyPayload);
    } else {
      handlePayloadChange({} as DailyFrequencyPayload);
    }
  }, [interval, quantityPerDate, handlePayloadChange])

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = parseInt((event.target as HTMLInputElement).value);
    if (input) {
      setInterval(input);
    } else {
      setInterval(undefined);
    }
  }

  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuantityPerDate(setQuantity(lessonCount, parseInt(event.target.value)));
  }

  return (
    <Box sx={styles.payloadSelectionBox}>
    <Typography component='div' variant='body2'>
      Every
      <TextField
        value={interval || ''}
        slotProps={
          {
            input: {
              sx: { '& .MuiInputBase-input': styles.inputText }
            }
          }
        }
        sx={styles.intervalInput}
        margin="normal"
        autoFocus={true}
        variant="outlined"
        onChange={handleInputChange}
      /> days
    </Typography>
    <QuantityPerDate
      quantityPerDate={quantityPerDate}
      quantityOnChange={handleQuantityChange}
      quantifier='day'
    />
    </Box>
  )
}

function Weekly({ payload, handlePayloadChange, lessonCount }: FrequencyPayloadProps) {
  const theme = useTheme();
  const styles = getStyles(theme);

  type daysSelectedState = {
    [key: string]: boolean;
  }
  const [days, setDays] = useState({} as daysSelectedState);
  const [interval, setInterval] = useState<number>();
  const [quantityPerDate, setQuantityPerDate] = useState<number>();

  useEffect(() => {
    const weeklyPayload = payload as WeeklyFrequencyPayload;
    if (weeklyPayload && weeklyPayload.interval && weeklyPayload.daysOfWeek) {
      setInterval(weeklyPayload.interval)
      const daysArray = weeklyPayload.daysOfWeek.split(',');
      let checked: daysSelectedState = {};
      daysArray.forEach(day => {
        checked = ({ ...checked, [day]: true })
      })
      setDays(checked);
    }
  }, [payload, lessonCount])

  useEffect(() => {

    if (payload && payload.quantityPerDate) {
      setQuantityPerDate(setQuantity(lessonCount, payload.quantityPerDate))
    }
  }, [payload, lessonCount])

  useEffect(() => {
    const daysOfWeek = Object.keys(days).filter(day => (
      days[day] === true
    )).join();

    if (!isEmpty(daysOfWeek) && interval && interval > 0 && quantityPerDate && quantityPerDate > 0) {
      const newPayload: WeeklyFrequencyPayload = { interval, daysOfWeek, quantityPerDate };
      handlePayloadChange(newPayload);
    } else {
      handlePayloadChange({} as WeeklyFrequencyPayload);
    }
  }, [days, interval, quantityPerDate, handlePayloadChange])

  const handleDaysChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDays({ ...days, [event.target.name]: event.target.checked });
  };

  const handleIntervalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInterval(parseInt(event.target.value));
  }

  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuantityPerDate(setQuantity(lessonCount, parseInt(event.target.value)));
  }

  return (
    <Box sx={styles.payloadSelectionBox}>
      <Typography component='div' variant='body2'>
        Repeat every
        <TextField
          value={interval || ''}
          slotProps={
            {
              input: {
                sx: { '& .MuiInputBase-input': styles.inputText }
              }
            }
          }
          sx={styles.intervalInput}
          margin="normal"
          autoFocus={true}
          variant="outlined"
          onChange={handleIntervalChange}
        /> week(s)
      </Typography>
      <FormGroup row>
        {getDays().map(day => (
          <FormControlLabel key={day}
            control={
              <Checkbox
                checked={!!days[day]}
                onChange={handleDaysChange}
                name={day}
                color="secondary"
              />
            }
            label={<Typography variant='body2' sx={{ textTransform: 'capitalize' }}>{day}</Typography>}
          />
        ))}
      </FormGroup>
      <QuantityPerDate
        quantityPerDate={quantityPerDate}
        quantityOnChange={handleQuantityChange}
        quantifier='day'
      />
    </Box>
  );
}

enum MonthFrequencyOptions {
  dayOfMonth = 1, 
  weekdayOfMonth = 2
}

function Monthly({ payload, handlePayloadChange, lessonCount }: FrequencyPayloadProps) {
  const theme = useTheme();
  const styles = getStyles(theme);

  const [dayOfMonth, setDayOfMonth] = useState<number>();
  const [weekdayOfMonth, setWeekdayOfMonth] = useState<string>();
  const [monthFrequencyType, setMonthFrequencyType] = useState<MonthFrequencyOptions>();
  const [interval, setInterval] = useState<number>();
  const [quantityPerDate, setQuantityPerDate] = useState<number>();

  useEffect(() => {    
    const monthlyPayload = payload as MonthlyFrequencyPayload;
    let monthFrequencyOpt: MonthFrequencyOptions = MonthFrequencyOptions.weekdayOfMonth;
    if (monthlyPayload && monthlyPayload.interval) {
      setInterval(monthlyPayload.interval)
      if (monthlyPayload.dayOfMonth) {
        setDayOfMonth(monthlyPayload.dayOfMonth > 31 ? 31 : monthlyPayload.dayOfMonth);
        monthFrequencyOpt = MonthFrequencyOptions.dayOfMonth;
      } else if (monthlyPayload.weekdayOfMonth) {
        setWeekdayOfMonth(monthlyPayload.weekdayOfMonth);
      }
      setQuantityPerDate(setQuantity(lessonCount, monthlyPayload.quantityPerDate))
    }
    setMonthFrequencyType(monthFrequencyOpt);
  }, [payload, lessonCount])

  useEffect(() => {
    if (
      ( (dayOfMonth && dayOfMonth > 0) || (weekdayOfMonth) ) &&
      interval && interval > 0 &&
      quantityPerDate && quantityPerDate > 0
    ) {
      const payload: MonthlyFrequencyPayload = { interval, dayOfMonth, weekdayOfMonth, quantityPerDate };
      handlePayloadChange(payload);
    } else {
      handlePayloadChange({} as MonthlyFrequencyPayload);
    }
  }, [dayOfMonth, weekdayOfMonth, interval, quantityPerDate, handlePayloadChange])

  const handleDayChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const interval = parseInt(event.target.value);
    setDayOfMonth(interval > 31 ? 31: interval);
  };

  const handleIntervalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInterval(parseInt(event.target.value));
  }

  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuantityPerDate(setQuantity(lessonCount, parseInt(event.target.value)));
  }

  const handleMonthFrequencyTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newMonthFrequency = parseInt(event.target.value) as MonthFrequencyOptions;
    setMonthFrequencyType(newMonthFrequency);
    if (newMonthFrequency === MonthFrequencyOptions.weekdayOfMonth) {
      setDayOfMonth(undefined);
    } else {
      setWeekdayOfMonth(undefined);
    }
  };

  return (
    <Box sx={styles.payloadSelectionBox}>
    <Typography component='div' variant='body2'>
    <RadioGroup
        row
        aria-label="monthFrequencyType"
        value={monthFrequencyType || 1}
        onChange={handleMonthFrequencyTypeChange}
    >
      <FormControlLabel
        value={MonthFrequencyOptions.weekdayOfMonth}
        control={<Radio />}
        label={<Typography
                variant="body2"
                sx={ monthFrequencyType === MonthFrequencyOptions.dayOfMonth ? styles.optionDisabled : undefined }
               >
                First
               </Typography>}
        sx={{ marginRight: 0 }}
      />
      <Select
            id="weekdayPicker"
            name="weekdayOfMonth"
            sx={styles.monthFrequencySelect}
            value={weekdayOfMonth || ''}
            onChange={ (e: any) => setWeekdayOfMonth(e.target.value) }
            disabled={monthFrequencyType !== MonthFrequencyOptions.weekdayOfMonth}
            variant="outlined"
      >
        <MenuItem value='' />
        {getDays().map((e) => (
            <MenuItem
              value={e}
              key={e}
              sx={{ padding: 0, textTransform: 'capitalize' }}
            >
              {e}
            </MenuItem>
          )
        )}
      </Select>
      <Typography component="span" sx={{ fontWeight: 'bold', margin: 'auto' }}>OR</Typography>
      <FormControlLabel
        value={MonthFrequencyOptions.dayOfMonth}
        control={<Radio />}
        label={<Typography
          variant="body2"
          sx={ monthFrequencyType === MonthFrequencyOptions.weekdayOfMonth ? styles.optionDisabled : undefined }
         >
          Day (1-31)
         </Typography>}
        sx={{ paddingLeft: '4px' }}
      />
      <TextField
        value={dayOfMonth || ''}
        slotProps={
          {
            input: {
              sx: { '& .MuiInputBase-input': styles.inputText }
            }
          }
        }
        sx={styles.intervalInput}
        margin="normal"
        autoFocus={true}
        variant="outlined"
        onChange={handleDayChange}
        disabled={monthFrequencyType !== MonthFrequencyOptions.dayOfMonth}
      />
    </RadioGroup>
    </Typography>
    <Typography component='div' variant='body2' sx={{ textAlign: 'center' }}>
      of every
      <TextField
        value={interval || ''}
        slotProps={
          {
            input: {
              sx: { '& .MuiInputBase-input': styles.inputText }
            }
          }
        }
        sx={styles.intervalInput}
        margin="normal"
        variant="outlined"
        onChange={handleIntervalChange}
      /> month(s)
    </Typography>
    <QuantityPerDate
      quantityPerDate={quantityPerDate}
      quantityOnChange={handleQuantityChange}
      quantifier='month'
    />
    </Box>
  );
}

interface QuantityProps {
  quantityPerDate?: number;
  quantityOnChange(event: React.ChangeEvent<HTMLInputElement>): void;
  quantifier: 'day' | 'month';
}
function QuantityPerDate({ quantityPerDate, quantityOnChange, quantifier }: QuantityProps) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const [quantity, setQuantity] = useState<number>();

  useEffect(() => {
    if (quantityPerDate) {
      setQuantity(quantityPerDate)
    } else {
      setQuantity(undefined)
    }
  }, [quantityPerDate]);

  return (

      <Typography sx={styles.quantitySelectionBox} component="div" variant="body2">
        Deliver
        <TextField
          value={quantity || ''}
          slotProps={
            {
              input: {
                sx: { '& .MuiInputBase-input': styles.inputText }
              }
            }
          }
          sx={styles.intervalInput}
          margin="normal"
          variant="outlined"
          onChange={quantityOnChange}
        />
        lesson(s) each {quantifier}
      </Typography>
  );
}
function setQuantity(lessonCount: number, quantity: number) {
  let quantityPerDate = quantity;
  if(lessonCount) {
    quantityPerDate = quantity > lessonCount ? lessonCount : quantity
  }
  return quantityPerDate;
}

interface FrequencyProps {
  frequencyType?: CourseFrequencyType;
  frequencyPayload?: CourseFrequencyPayload;
  handleTypeChange(type: CourseFrequencyType): void;
  handlePayloadChange(payload: CourseFrequencyPayload): void;
  lessonCount: number;
}

export default function Frequency({ frequencyType, frequencyPayload, handleTypeChange, handlePayloadChange, lessonCount }: FrequencyProps) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const [frequency, setFrequency] = React.useState('' as CourseFrequencyType);
  useEffect(() => {
    if (frequencyType) {
     setFrequency(frequencyType);
    }
  }, [frequencyType])

  useEffect(() => {
    if (frequency) {
      handleTypeChange(frequency);
      handlePayloadChange({} as CourseFrequencyPayload);
    }
  }, [frequency, handleTypeChange, handlePayloadChange]);


  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const typeKey = Object.keys(CourseFrequencyType).find(type =>
        type.toLowerCase() === event.target.value
    );

    if (typeKey) {
      setFrequency(CourseFrequencyType[typeKey as keyof typeof CourseFrequencyType]);
    }
  };

  let FrequencySelection: FunctionComponent<FrequencyPayloadProps>;

  switch (frequency) {
    case CourseFrequencyType.Daily:
        FrequencySelection = Daily;
      break;
    case CourseFrequencyType.Weekly:
        FrequencySelection = Weekly;
      break;
    case CourseFrequencyType.Monthly:
        FrequencySelection = Monthly;
      break;
    default:
        FrequencySelection = Daily;
  }

  return (
    <Grid container size={12} sx={styles.noWrapContainer}>
      <Grid size={2}>
        <Typography>Frequency</Typography>
      </Grid>
      <Grid size={2}>
        <FormControl variant="standard">
          <RadioGroup
            aria-label="frequency"
            value={frequency}
            onChange={handleChange}
          >
            {Object.values(CourseFrequencyType).map((frequency) => (
              <FormControlLabel
                key={frequency}
                value={frequency}
                control={<Radio />}
                label={
                  <Typography
                    variant="body2"
                    sx={{ textTransform: "capitalize" }}
                  >
                    {frequency}
                  </Typography>
                }
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid size={1}>
        <Divider orientation="vertical" sx={{ width: 5 }} />
      </Grid>
      <Grid size={7}>
        {frequency && (
          <FrequencySelection
            payload={frequencyPayload}
            handlePayloadChange={handlePayloadChange}
            lessonCount={lessonCount}
          />
        )}
      </Grid>
    </Grid>
  );
}